/**
 * ハイライトリール（旅のダイジェスト映像）
 *
 * オープニング → スポットごとの章 → エンディングの構成で再生する。
 * 写真は Ken Burns でゆっくり動かし、その瞬間の位置・標高・滞在時間を重ねて
 * 「ただのスライドショー」にならないようにしている。
 */

import { ArrowRight, Footprints, Loader2, MapPin, Mountain, Pause, Play, Route, Sparkles, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { TripDay, TripDetail, TripRoutePoint } from '@/api/types/trip'
import { Button } from '@/components/ui/button'
import { formatTimeJST } from '@/lib/activity-format-utils'
import { cn } from '@/lib/utils'

import type { ReelImage } from './trip-reel-images'
import { usePlaceImages, useReelImages } from './trip-reel-images'
import { ReelChapterMap } from './trip-reel-map'
import type { ChapterScene, EndingScene, OpeningScene, PhotoScene, Scene } from './trip-reel-scenes'
import { buildScenes } from './trip-reel-scenes'
import { dateRangeLabel, distanceLabel, durationLabel } from './trip-utils'

/** 進捗バーの更新間隔 */
const TICK_MS = 50
/** 画像の読み込みをこれ以上待たずに先へ進む上限 */
const MAX_WAIT_MS = 8000
/** 画面比との乖離がこれを超えたら、切りすぎを避けて写真全体を見せる */
const MAX_CROP_RATIO = 0.25
/** ボタンで順に切り替える再生速度 */
const SPEEDS: number[] = [1, 1.5, 2, 0.5]

type Props = {
  trip: TripDetail
  days: TripDay[]
  routePoints: TripRoutePoint[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const TripHighlightReel = ({ trip, days, routePoints, open, onOpenChange }: Props) => {
  const scenes = useMemo(
    () =>
      buildScenes({
        trip,
        days,
        routePoints,
        dateRange: dateRangeLabel(trip),
        duration: durationLabel(trip),
      }),
    [trip, days, routePoints],
  )

  const [index, setIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [waitedMs, setWaitedMs] = useState(0)
  const [speed, setSpeed] = useState(1)

  const lastIndex = Math.max(scenes.length - 1, 0)
  const current = scenes.at(index) ?? null
  const isFinished = !isPlaying && index >= lastIndex && elapsedMs >= (current?.durationMs ?? 0)

  // シーン index と 1:1 で対応させ、先読み対象を index から引けるようにする
  // 章は到着ピンにはめる写真を持つので、章の時点で読み終わっているようにする
  const sceneAssetIds = useMemo(
    () =>
      scenes.map((s) => {
        if (s.kind === 'photo') return s.asset.id
        if (s.kind === 'opening') return s.coverAssetId
        if (s.kind === 'chapter') return s.photoAssetId
        return null
      }),
    [scenes],
  )
  const { getImage, isCurrentSettled } = useReelImages({ assetIds: sceneAssetIds, index, enabled: open })
  const isWaiting = !isCurrentSettled && waitedMs < MAX_WAIT_MS

  // Place 画像は章ごとに逆引ければよいので、重複を除いてまとめて先読みする
  const placeImagePaths = useMemo(
    () => [
      ...new Set(
        scenes.map((s) => (s.kind === 'chapter' ? s.placeImagePath : null)).filter((p): p is string => p !== null),
      ),
    ],
    [scenes],
  )
  const getPlaceImage = usePlaceImages({ paths: placeImagePaths, enabled: open })

  // 位置が判明しているシーンだけを地図の軌跡に使う
  const mapPath = useMemo(
    () =>
      scenes
        .map((s) => (s.kind === 'photo' || s.kind === 'chapter' ? s.position : null))
        .filter((p): p is google.maps.LatLngLiteral => p !== null),
    [scenes],
  )
  const chapter = current?.kind === 'chapter' ? current : null
  // Place の写真を優先し、無ければ旅中に撮った写真で代用する
  const chapterPhotoUrl = chapter
    ? (getPlaceImage(chapter.placeImagePath) ?? getImage(chapter.photoAssetId)?.url ?? null)
    : null
  const hasMap = mapPath.length > 0

  const goTo = useCallback((next: number) => {
    setIndex(next)
    setElapsedMs(0)
    setWaitedMs(0)
  }, [])

  useEffect(() => {
    if (!open) return
    goTo(0)
    setIsPlaying(true)
  }, [open, goTo])

  // 画像が届くまでは進行を止める（映らないまま次のシーンに行かないように）
  useEffect(() => {
    if (!open || !isPlaying || isCurrentSettled) return

    const timer = window.setInterval(() => setWaitedMs((prev) => prev + TICK_MS), TICK_MS)
    return () => window.clearInterval(timer)
  }, [open, isPlaying, isCurrentSettled])

  useEffect(() => {
    if (!open || !isPlaying || !current || isWaiting) return

    const timer = window.setInterval(() => {
      setElapsedMs((prev) => {
        // 間隔を変えると進捗バーがカクツくので、進む量の方を速度倍にする
        const next = prev + TICK_MS * speed
        if (next < current.durationMs) return next

        if (index >= lastIndex) {
          setIsPlaying(false)
          return current.durationMs
        }
        setIndex((i) => i + 1)
        setWaitedMs(0)
        return 0
      })
    }, TICK_MS)

    return () => window.clearInterval(timer)
  }, [open, isPlaying, current, index, lastIndex, isWaiting, speed])

  const goPrev = useCallback(() => {
    // 再生中に戻る場合、まずは今のシーンを頭出しする
    setElapsedMs((prev) => {
      if (prev > 600) return 0
      setIndex((i) => Math.max(0, i - 1))
      setWaitedMs(0)
      return 0
    })
  }, [])

  const goNext = useCallback(() => {
    if (index >= lastIndex) {
      setIsPlaying(false)
      return
    }
    goTo(index + 1)
  }, [index, lastIndex, goTo])

  const togglePlay = useCallback(() => {
    if (isFinished) {
      goTo(0)
      setIsPlaying(true)
      return
    }
    setIsPlaying((prev) => !prev)
  }, [isFinished, goTo])

  const seekTo = useCallback(
    (next: number) => {
      goTo(next)
      // 再生完了後にシークしたらそのまま再生を再開する
      if (isFinished) setIsPlaying(true)
    },
    [goTo, isFinished],
  )

  const cycleSpeed = useCallback(() => {
    setSpeed((prev) => SPEEDS.at((SPEEDS.indexOf(prev) + 1) % SPEEDS.length) ?? 1)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') goPrev()
      if (event.key === 'ArrowRight') goNext()
      if (event.key === ' ') togglePlay()
      if (event.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, goPrev, goNext, togglePlay, onOpenChange])

  if (!open) return null

  if (scenes.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-black text-white">
        <p className="text-sm text-white/70">ダイジェストにできる記録がありません</p>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          閉じる
        </Button>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="absolute inset-x-0 top-0 z-20 space-y-2 bg-linear-to-b from-black/70 to-transparent px-3 pt-2 pb-6">
        <SegmentedProgress scenes={scenes} index={index} elapsedMs={elapsedMs} onSeek={seekTo} />
        <div className="flex items-center justify-between text-white">
          <span className="text-xs tabular-nums text-white/70">
            {index + 1} / {scenes.length}
          </span>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="p-2! text-white">
            <X className="size-5" />
          </Button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        {current?.kind === 'opening' && (
          <OpeningCard
            scene={current}
            image={getImage(current.coverAssetId)}
            isPlaying={isPlaying && !isWaiting}
            speed={speed}
          />
        )}
        {current?.kind === 'photo' && (
          <PhotoSlide
            scene={current}
            image={getImage(current.asset.id)}
            isPlaying={isPlaying && !isWaiting}
            speed={speed}
          />
        )}
        {current?.kind === 'ending' && <EndingCard scene={current} />}

        {/* 地図はマウントしたままにする
            （シーンごとに作り直すと Google Maps のマップロードが都度発生するため） */}
        {hasMap && (
          <div
            className={cn(
              'pointer-events-none absolute inset-0 transition-opacity duration-300',
              chapter ? 'opacity-100' : 'opacity-0',
            )}
          >
            <ReelChapterMap
              scene={chapter}
              arrivalPhotoUrl={chapterPhotoUrl}
              initialCenter={mapPath.at(0)}
              speed={speed}
            />
          </div>
        )}

        {chapter && <ChapterOverlay scene={chapter} />}

        {isWaiting && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40">
            <Loader2 className="size-8 animate-spin text-white/80" />
          </div>
        )}

        <button
          type="button"
          onClick={goPrev}
          className="absolute inset-y-0 left-0 z-10 w-1/4 p-0! text-transparent"
          aria-label="前のシーン"
        />
        <button
          type="button"
          onClick={goNext}
          className="absolute inset-y-0 right-0 z-10 w-1/4 p-0! text-transparent"
          aria-label="次のシーン"
        />
      </div>

      <div className="absolute inset-x-0 bottom-0 z-20 flex items-center gap-2 bg-linear-to-t from-black/70 to-transparent px-3 pt-8 pb-5">
        <Button variant="ghost" size="icon" onClick={togglePlay} className="p-2! text-white">
          {isPlaying ? <Pause className="size-5" /> : <Play className="size-5" />}
        </Button>
        <Button
          variant="ghost"
          onClick={cycleSpeed}
          className="px-2! py-1! text-xs font-bold tabular-nums text-white"
          aria-label="再生速度を切り替え"
        >
          {speed}×
        </Button>
        {current?.kind === 'photo' && <PhotoCaption scene={current} />}
      </div>
    </div>
  )
}

/** 進捗バーを「日 > スポット > シーン」の入れ子で描くためのグルーピング */
type ProgressPlace = { indices: number[] }
type ProgressDay = { label: string | null; places: ProgressPlace[] }

const buildProgressDays = (scenes: Scene[]): ProgressDay[] => {
  const days: ProgressDay[] = []
  let currentDayNumber: number | null | undefined

  for (const [i, scene] of scenes.entries()) {
    const dayNumber = scene.kind === 'chapter' || scene.kind === 'photo' ? scene.dayNumber : null
    if (days.length === 0 || dayNumber !== currentDayNumber) {
      days.push({ label: dayNumber === null ? null : `${dayNumber}日目`, places: [] })
      currentDayNumber = dayNumber
    }

    const day = days.at(-1)
    if (!day) continue

    // 章シーンがスポットの区切り。章を持たない写真は直前の区切りに続ける
    if (scene.kind === 'chapter' || day.places.length === 0) day.places.push({ indices: [] })
    day.places.at(-1)?.indices.push(i)
  }

  return days
}

/** Instagram Stories 風のセグメント進捗バー（タップでそのシーンへ移動） */
const SegmentedProgress = ({
  scenes,
  index,
  elapsedMs,
  onSeek,
}: {
  scenes: Scene[]
  index: number
  elapsedMs: number
  onSeek: (index: number) => void
}) => {
  const days = useMemo(() => buildProgressDays(scenes), [scenes])

  return (
    <div className="flex items-end gap-2">
      {days.map((day, d) => {
        const dayCount = day.places.reduce((sum, place) => sum + place.indices.length, 0)
        return (
          // シーン数に比例した幅にして、日の長さが一目で分かるようにする
          <div key={d} className="min-w-0" style={{ flexGrow: dayCount, flexBasis: 0 }}>
            <div className="mb-1 truncate text-center text-[10px] leading-none text-white/50">
              {day.label ?? '\u00a0'}
            </div>
            <div className="flex gap-1">
              {day.places.map((place, p) => (
                <div key={p} className="flex min-w-0 gap-px" style={{ flexGrow: place.indices.length, flexBasis: 0 }}>
                  {place.indices.map((sceneIndex) => (
                    <ProgressSegment
                      key={sceneIndex}
                      scene={scenes.at(sceneIndex)}
                      sceneIndex={sceneIndex}
                      index={index}
                      elapsedMs={elapsedMs}
                      onSeek={onSeek}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const ProgressSegment = ({
  scene,
  sceneIndex,
  index,
  elapsedMs,
  onSeek,
}: {
  scene: Scene | undefined
  sceneIndex: number
  index: number
  elapsedMs: number
  onSeek: (index: number) => void
}) => {
  if (!scene) return null

  const ratio = sceneIndex < index ? 1 : sceneIndex > index ? 0 : Math.min(elapsedMs / scene.durationMs, 1)

  return (
    <button
      type="button"
      onClick={() => onSeek(sceneIndex)}
      // バー自体は 2px しかないので、上下に余白を取ってタッチ領域を確保する
      className="min-w-0 flex-1 cursor-pointer py-2"
      aria-label={`シーン ${sceneIndex + 1} へ移動`}
    >
      <div className="h-0.5 w-full overflow-hidden rounded-full bg-white/30">
        <div className="h-full bg-white" style={{ width: `${ratio * 100}%` }} />
      </div>
    </button>
  )
}

/**
 * Ken Burns エフェクト。
 * シーンごとに寄り引きの向きを変えて、静止画の退屈さを抑える。
 */
/** 全画面表示領域の縦横比（回転・リサイズに追従する） */
const useViewportAspect = () => {
  const [aspect, setAspect] = useState(() => window.innerWidth / window.innerHeight)

  useEffect(() => {
    const onResize = () => setAspect(window.innerWidth / window.innerHeight)
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [])

  return aspect
}

/**
 * Ken Burns エフェクト。
 * 画面比に近い写真は全画面で見せ、乖離が大きい写真（縦長画面での横位置写真など）は
 * 大部分が切れてしまうので、ぼかした自分自身を背景に敷いて全体を表示する。
 */
const KenBurns = ({
  image,
  alt,
  durationMs,
  seed,
  isPlaying,
  speed,
}: {
  image: ReelImage | null
  alt: string
  durationMs: number
  seed: number
  isPlaying: boolean
  speed: number
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [animation, setAnimation] = useState<Animation | null>(null)
  const viewportAspect = useViewportAspect()

  const isCropSevere = image !== null && Math.abs(image.aspect - viewportAspect) / viewportAspect > MAX_CROP_RATIO

  useEffect(() => {
    const element = ref.current
    if (!element || !image) return

    // 全体表示のときに大きく動かすと結局はみ出るので、寄りから引きへ戻すだけにする
    const frames = isCropSevere
      ? [{ transform: 'scale(1.05)' }, { transform: 'scale(1)' }]
      : (() => {
          const zoomIn = seed % 2 === 0
          const shiftX = seed % 3 === 0 ? 2.5 : -2.5
          const shiftY = seed % 5 === 0 ? -2 : 2
          const wide = `scale(1.14) translate(${shiftX}%, ${shiftY}%)`
          const tight = 'scale(1.02) translate(0, 0)'
          return [{ transform: zoomIn ? tight : wide }, { transform: zoomIn ? wide : tight }]
        })()

    const created = element.animate(frames, {
      duration: durationMs + 800,
      easing: 'linear',
      fill: 'forwards',
    })
    setAnimation(created)

    return () => {
      created.cancel()
      setAnimation(null)
    }
  }, [image, durationMs, seed, isCropSevere])

  // 一時停止中に写真だけ止まってズームが進み続けないようにする
  useEffect(() => {
    if (!animation) return
    // 再生位置を保ったまま速度だけ変えたいので duration ではなく再生レートを使う
    animation.updatePlaybackRate(speed)
    if (isPlaying) animation.play()
    else animation.pause()
  }, [animation, isPlaying, speed])

  return (
    <div className="absolute inset-0 overflow-hidden bg-neutral-900">
      {image && isCropSevere && (
        <img
          src={image.url}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-125 object-cover opacity-50 blur-2xl"
        />
      )}
      <div ref={ref} className="relative h-full w-full will-change-transform">
        {image && (
          <img
            src={image.url}
            alt={alt}
            className={cn('h-full w-full', isCropSevere ? 'object-contain' : 'object-cover')}
            decoding="async"
          />
        )}
      </div>
    </div>
  )
}

const OpeningCard = ({
  scene,
  image,
  isPlaying,
  speed,
}: {
  scene: OpeningScene
  image: ReelImage | null
  isPlaying: boolean
  speed: number
}) => (
  <div className="absolute inset-0">
    {scene.coverAssetId && (
      <>
        <KenBurns
          image={image}
          alt={scene.title}
          durationMs={scene.durationMs}
          seed={0}
          isPlaying={isPlaying}
          speed={speed}
        />
        <div className="absolute inset-0 bg-black/55" />
      </>
    )}
    <div className="relative flex h-full flex-col items-center justify-center gap-3 px-8 text-center text-white">
      <Sparkles className="size-6 text-amber-300" />
      <p className="text-xs tracking-[0.3em] text-white/70">HIGHLIGHTS</p>
      <h2 className="wrap-break-word text-3xl font-bold">{scene.title}</h2>
      <p className="text-sm text-white/80">{scene.dateRange}</p>
      <div className="flex flex-wrap justify-center gap-1.5">
        <Chip>{scene.duration}</Chip>
        {scene.prefectures.map((p) => (
          <Chip key={p}>{p}</Chip>
        ))}
      </div>
    </div>
  </div>
)

const ChapterOverlay = ({ scene }: { scene: ChapterScene }) => (
  <>
    {/* 中央は地図を見せたいので、文字は上下の帯に逃がす */}
    <div className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-linear-to-b from-black/85 to-transparent" />
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-linear-to-t from-black/85 to-transparent" />

    <div className="pointer-events-none absolute inset-x-0 top-14 flex flex-col items-center gap-1 px-8 text-center text-white">
      <p className="text-xs tracking-[0.25em] text-amber-300">DAY {scene.dayNumber}</p>
      <h3 className="wrap-break-word text-2xl font-bold drop-shadow-lg">{scene.placeName}</h3>
    </div>

    <div className="pointer-events-none absolute inset-x-0 bottom-20 flex flex-col items-center gap-2 px-6 text-white">
      <div className="flex max-w-full items-center gap-2 rounded-full bg-black/65 px-3 py-1.5">
        <span className="size-2 shrink-0 rounded-full bg-slate-400" />
        <span className="max-w-24 truncate text-xs text-white/80">{scene.fromPlaceName ?? '旅のはじまり'}</span>
        <ArrowRight className="size-4 shrink-0 text-amber-300" />
        <span className="size-2 shrink-0 rounded-full bg-red-500" />
        <span className="max-w-28 truncate text-xs font-bold">{scene.placeName}</span>
      </div>
      <div className="flex flex-wrap justify-center gap-1.5">
        {scene.legDistanceKm !== null && <Chip>{distanceLabel(scene.legDistanceKm)} 移動</Chip>}
        {scene.stayLabel && <Chip>{scene.stayLabel}</Chip>}
        <Chip>写真 {scene.photoCount} 枚</Chip>
        {scene.steps !== null && <Chip>この日 {scene.steps.toLocaleString()} 歩</Chip>}
      </div>
    </div>
  </>
)

const PhotoSlide = ({
  scene,
  image,
  isPlaying,
  speed,
}: {
  scene: PhotoScene
  image: ReelImage | null
  isPlaying: boolean
  speed: number
}) => (
  <>
    <KenBurns
      key={scene.asset.id}
      image={image}
      alt={scene.asset.originalFileName}
      durationMs={scene.durationMs}
      seed={scene.asset.fileCreatedAt.length + scene.dayNumber}
      isPlaying={isPlaying}
      speed={speed}
    />
    <div className="absolute top-16 left-4 z-10 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white">
      DAY {scene.dayNumber}
    </div>
  </>
)

/** 写真だけでは分からない情報（場所・時刻・標高）を重ねる */
const PhotoCaption = ({ scene }: { scene: PhotoScene }) => (
  <div className="min-w-0 flex-1 text-white">
    <p className="truncate text-sm font-medium">{scene.placeName ?? '移動中'}</p>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-white/70">
      <span className="tabular-nums">{formatTimeJST(scene.asset.fileCreatedAt)}</span>
      {scene.altitude !== null && (
        <span className="flex items-center gap-1 tabular-nums">
          <Mountain className="size-3" />
          {Math.round(scene.altitude)} m
        </span>
      )}
      {scene.asset.type === 'VIDEO' && <span className="text-amber-300">VIDEO</span>}
    </div>
  </div>
)

const EndingCard = ({ scene }: { scene: EndingScene }) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-5 px-8 text-white">
    <div className="text-center">
      <p className="text-xs tracking-[0.3em] text-white/60">THE END</p>
      <h3 className="wrap-break-word text-xl font-bold">{scene.title}</h3>
    </div>

    <div className="grid w-full max-w-xs grid-cols-2 gap-3">
      <SummaryCell icon={<Footprints className="size-4" />} label="歩数" value={scene.totalSteps.toLocaleString()} />
      <SummaryCell icon={<Route className="size-4" />} label="移動" value={distanceLabel(scene.travelDistanceKm)} />
      <SummaryCell icon={<MapPin className="size-4" />} label="スポット" value={`${scene.placeCount}`} />
      <SummaryCell
        icon={<Mountain className="size-4" />}
        label="最高標高"
        value={scene.maxAltitudeM === null ? '—' : `${Math.round(scene.maxAltitudeM)} m`}
      />
    </div>

    <div className="flex flex-wrap justify-center gap-1.5">
      {scene.newPlaceCount > 0 && <Chip>初めての場所 {scene.newPlaceCount}</Chip>}
      <Chip>写真・動画 {scene.photoCount}</Chip>
      {scene.prefectures.map((p) => (
        <Chip key={p}>{p}</Chip>
      ))}
    </div>
  </div>
)

const SummaryCell = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="rounded-lg bg-white/10 px-3 py-2">
    <div className="flex items-center gap-1.5 text-xs text-white/60">
      {icon}
      {label}
    </div>
    <p className="truncate text-lg font-bold tabular-nums">{value}</p>
  </div>
)

const Chip = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={cn('rounded-full bg-white/15 px-2.5 py-1 text-xs text-white', className)}>{children}</span>
)
